import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  assessMappingFamilyRequirementsGrpc,
  type FamilyScopeAssessment,
} from '../../../shared/api/grpc/familyRequirementAssessmentClient';
import { sanitizeRevitParameterNames } from '../../../shared/lib/revitParameterNames';
import type {
  MappingParameterAssessment,
  MappingParameterScopeRequirement,
} from '../lib/mappingReadinessFamilyExclusions';
import { buildMappingFamilyRequirementAssessmentInputs } from '../lib/mappingReadinessSnapshotRequest';
import { traceMappingFlow, warnMappingFlow } from '../lib/mappingTrace';
import type { MappingCatalogRefreshSuspensionRef } from './useMappingParameterCatalogs';

interface MappingFamilyRequirementBackendAssessmentData {
  assessments: MappingParameterAssessment[];
}

interface MappingFamilyRequirementBackendAssessmentState
  extends MappingFamilyRequirementBackendAssessmentData {
  scopeKey: string;
  isLoading: boolean;
  isReady: boolean;
  error: string | null;
}

const EMPTY_DATA: MappingFamilyRequirementBackendAssessmentData = { assessments: [] };

const TARGET_KINDS: ReadonlySet<string> = new Set([
  'editableFamilyCategories',
  'familyObjectTypes',
  'projectCategories',
]);

export function toMappingParameterAssessments(
  assessments: FamilyScopeAssessment[],
  requirements: MappingParameterScopeRequirement[]
): MappingParameterAssessment[] {
  const targetKindByKey = new Map(
    requirements.map((requirement) => [requirement.key, requirement.target.kind])
  );
  return assessments.map((assessment) => {
    const targetKind = targetKindByKey.get(assessment.requirementKey) ??
      (TARGET_KINDS.has(assessment.targetKind)
        ? (assessment.targetKind as MappingParameterAssessment['targetKind'])
        : 'familyObjectTypes');
    return {
      requirementKey: assessment.requirementKey,
      satisfied: assessment.satisfied,
      status: assessment.status,
      targetKind,
      matchingEntityCount: assessment.matchingEntityCount,
      conflicts: assessment.conflicts,
    };
  });
}

/**
 * Fetches the backend-authoritative family-scope verdicts for the mapping
 * readiness requirements. Keyed by project + model version + the serialized
 * requirement set, so a post-repair model-version bump re-fetches with the
 * same engine that produced the pre-repair verdict.
 */
export function useMappingFamilyRequirementBackendAssessment(input: {
  enabled: boolean;
  runtimeAvailable: boolean;
  projectKey: string | null;
  modelVersion: number | null;
  requirements: MappingParameterScopeRequirement[];
  refreshSuspendedRef?: MappingCatalogRefreshSuspensionRef;
}) {
  const familyRequirements = useMemo(
    () => input.requirements.filter(
      (requirement) => requirement.target.kind !== 'projectCategories'
    ),
    [input.requirements]
  );
  const requestInputs = useMemo(
    () => buildMappingFamilyRequirementAssessmentInputs(familyRequirements),
    [familyRequirements]
  );
  const scopeKey =
    input.enabled && input.runtimeAvailable && input.projectKey && requestInputs.length > 0
      ? `${input.projectKey}::${input.modelVersion ?? 'unknown'}::${JSON.stringify(requestInputs)}`
      : '';
  const [state, setState] = useState<MappingFamilyRequirementBackendAssessmentState>({
    scopeKey: '',
    isLoading: false,
    isReady: false,
    error: null,
    ...EMPTY_DATA,
  });
  const sequenceRef = useRef(0);
  const pendingRef = useRef<{ scopeKey: string; request: Promise<MappingFamilyRequirementBackendAssessmentData> } | null>(null);

  const readAssessments = useCallback(async (): Promise<MappingFamilyRequirementBackendAssessmentData> => {
    if (!scopeKey) {
      setState({ scopeKey, isLoading: false, isReady: true, error: null, ...EMPTY_DATA });
      return EMPTY_DATA;
    }
    if (pendingRef.current?.scopeKey === scopeKey) {
      return pendingRef.current.request;
    }

    const sequence = ++sequenceRef.current;
    setState((current) => ({
      ...(current.scopeKey === scopeKey ? current : EMPTY_DATA),
      scopeKey,
      isLoading: true,
      isReady: false,
      error: null,
    }));
    traceMappingFlow('mapping family requirement assessment fetch', {
      projectKey: input.projectKey,
      modelVersion: input.modelVersion,
      requirementCount: requestInputs.length,
    });
    const pending = assessMappingFamilyRequirementsGrpc(requestInputs).then((result) => {
      if (!result.success) {
        throw new Error(result.error || 'Mapping family requirement assessment failed');
      }
      return {
        assessments: toMappingParameterAssessments(
          result.assessments ?? [],
          familyRequirements
        ),
      };
    });
    pendingRef.current = { scopeKey, request: pending };
    try {
      const data = await pending;
      if (sequence === sequenceRef.current) {
        setState({ scopeKey, isLoading: false, isReady: true, error: null, ...data });
      }
      return data;
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'Mapping family requirement assessment failed';
      if (sequence === sequenceRef.current) {
        setState({ scopeKey, isLoading: false, isReady: false, error: message, ...EMPTY_DATA });
      }
      warnMappingFlow('mapping family requirement assessment failed', {
        projectKey: input.projectKey,
        modelVersion: input.modelVersion,
        message,
      });
      throw error;
    } finally {
      if (pendingRef.current?.request === pending) pendingRef.current = null;
    }
  }, [familyRequirements, input.modelVersion, input.projectKey, requestInputs, scopeKey]);

  useEffect(() => {
    if (!scopeKey) return;
    if (
      input.refreshSuspendedRef?.current.active &&
      input.refreshSuspendedRef.current.projectKey === input.projectKey
    ) return;
    if (state.scopeKey === scopeKey && (state.isLoading || state.isReady || state.error)) return;
    void readAssessments().catch(() => undefined);
  }, [input.projectKey, input.refreshSuspendedRef, readAssessments, scopeKey, state]);

  const matchesScope = state.scopeKey === scopeKey;
  const assessments = matchesScope ? state.assessments : EMPTY_DATA.assessments;
  const satisfiedNames = useMemo(() => {
    const satisfiedKeys = new Set(
      assessments
        .filter((assessment) => assessment.satisfied)
        .map((assessment) => assessment.requirementKey)
    );
    return sanitizeRevitParameterNames(
      familyRequirements
        .filter((requirement) => satisfiedKeys.has(requirement.key))
        .map((requirement) => requirement.parameterName)
    );
  }, [assessments, familyRequirements]);
  return {
    familyRequirements,
    assessments,
    satisfiedNames,
    isLoading: matchesScope && state.isLoading,
    isReady: !scopeKey || (matchesScope && state.isReady),
    error: matchesScope ? state.error : null,
    refresh: readAssessments,
  };
}
